import * as React from "react";
import {RootState} from "Redux/Reducers/_RootReducer";
import {withTranslation} from "react-i18next";
import {connect} from "react-redux";
import {Footer, InfinityReactTable, InfinityScrollParams} from "components/common/InfinityReactTable";
import {
    Cell,
    checkPageQuery,
    ContractHistoryList,
    Filter,
    injectThProps,
    IReactTableWrapper
} from "HelperFunctions/Lists";
import * as ErrorActions from "Redux/Actions/ErrorActions";
import {EnumTypes as ErrorEnumType} from "Models/EnumTypes/ErrorEnumTypes";
import i18n from "Translations/_i18n";
import * as ContractModule from "Modules/ContractModule";
import {Types} from "Modules/ContractModule";
import {createSerachQuery} from "Connectors/_main";
import * as InfinityTableModule from "Modules/InfinityTableModule";
import * as Dictionary from "Models/Dictionary";
import * as UserModule from "Modules/UserModule";
import {ActiveType} from "Models/EnumTypes/ContractEnumTypes";
import ContractHistoryDto = Types.ContractHistoryDto;

type ConnectedDispatch = {
    getContractList: (queryParams?: URLSearchParams, filterQuery?: boolean) => Function;
    getContractHistory: (id: number) => Function;
    setActive: (contractNumber: string) => Function;
    copiedToClipboard: (text) => Function;
    t?: Function;
    setInfinityTableLoading: Function;
    setCurrentContractVin: Function;
};

type ConnectedProps = {
    contractTotalCount: number;
    contractList: Array<ContractModule.Types.ContractSingleDto>;
    contractHistory: Array<ContractHistoryDto>;
    selectedContractVin: string;
};

type S = {
    selected: number;
    activeSorting: string;
    activeFilters: Array<{ id: string, value: any }>;
    expanded: any;
};

class ContractList extends React.PureComponent<ConnectedProps & ConnectedDispatch, S> implements IReactTableWrapper {
    constructor(props: ConnectedProps & ConnectedDispatch) {
        super(props);
        this.state = {
            selected: null,
            activeSorting: '',
            activeFilters: [],
            expanded: {}
        };

        this.getContractList = this.getContractList.bind(this);
        this.getTrProps = this.getTrProps.bind(this);
        this.handleRowExpanded = this.handleRowExpanded.bind(this);

        this.infinityScrollParams = new InfinityScrollParams(35);
        this.infinityScrollParams.setWaypointRef(React.createRef());
    }

    private infinityScrollParams: InfinityScrollParams;

    private static checkPageQueryLocal(tableFiltered: Array<{ id: string; value: any }>, statePage: any, itemsPerLoad: number, itemsFirstLoad: number) {
        const tempFiltered = tableFiltered || [];
        return checkPageQuery(tempFiltered, statePage, itemsPerLoad, itemsFirstLoad);
    }

    private async getContractList(tableState: any, scrollLoad: boolean = false, firstLoadCount: number = null) {
        /*
         * Pierwsze woła onFetchData (scrollLoad = false)
         * Zawsze gdy woła onFetchData to resetuje wskazniki infinityScrolla bo to beda filtry i sortowanie
         * Kiedy infinity scroll się ładuje (scrollLoad = true) to currentPage sie zwieksza, a parametr state jest nullem.
         */

        if (scrollLoad && this.props.contractList.length >= this.props.contractTotalCount) {
            return 0;
        }

        const {activeFilters, currentPage, itemsPerLoad, itemsFirstLoad} = this.infinityScrollParams.prepareLoadParams(tableState, scrollLoad);

        // Jeśli w wywołaniu metody będzie przekazana wartość tzn. że dynamicznie była liczona ilość wierszy do pierwszego ładowania.
        // Wykonane ze względu na potrzebę sterowania tym w sytuacji np.
        const newItemsFirstLoad = firstLoadCount || itemsFirstLoad;

        this.props.setInfinityTableLoading(true);

        if (activeFilters.length === 1 && activeFilters[0].id === 'orderNumber') {
            activeFilters.push({id: 'sort_orderNumber', value: 'ASCENDING'});
        }

        //***** Naprawa błędnego filtrowania razem z sortowaniem
        Filter.FilterSortingFix.call(this, activeFilters);
        //******
        const activeFiltersPrepared = activeFilters.filter(element => element.value);

        // jeśli jest zaznaczona umowa w tabeli umów to filtruje po vinie pojazdu z tamtej umowy
        if (this.props.selectedContractVin) {
            activeFiltersPrepared.push({id: 'vin', value: this.props.selectedContractVin});
            this.props.setCurrentContractVin(null);
        }

        const query = ContractList.checkPageQueryLocal(
            activeFiltersPrepared,
            currentPage,
            itemsPerLoad,
            newItemsFirstLoad
        );
        const params = createSerachQuery(query);

        this.props.setActive(null);

        await this.props.getContractList(params, !scrollLoad);

        this.props.setInfinityTableLoading(false);

        this.setState({
            selected: null,
            activeFilters: activeFiltersPrepared,
            expanded: {}
        });
    }

    private getTrProps = (state, rowInfo: { original: ContractModule.Types.ContractSingleDto; [name: string]: any }) => {
        const trClass = rowInfo && rowInfo.index === this.state.selected ? 'active' : '';
        return {
            onClick: () => {
                this.setState({selected: rowInfo.index});
                this.props.setActive(rowInfo.original.contractNumber);
            },
            className: trClass
        };
    };

    private async handleRowExpanded(newExpanded, index, event) {
        if (!this.state.expanded[index]) {
            await this.props.getContractHistory(this.props.contractList[index].id);
            this.setState({expanded: {[index]: true}});
        } else {
            this.setState({expanded: {[index]: false}});
        }
    };

    public setActiveSorting = (e: any, colId: string, onChange: Function) => {
        this.setState({
                activeSorting: colId
            }, () => onChange(e)
        );
    };

    public render() {
        return <div className="main__content main__content--no-padding">
            <div className="table__content" data-testid="contract-table"
                 ref={this.infinityScrollParams.getWaypointRef()}>
                <InfinityReactTable
                    data={this.props.contractList}
                    columns={this.columns}
                    onFetchData={this.getContractList}
                    getTrProps={this.getTrProps}
                    defaultPageSize={this.infinityScrollParams.getItemsFirstLoad()}
                    filterable={true}
                    resizable={true}
                    getTheadThProps={injectThProps}
                    waypointRef={this.infinityScrollParams.getWaypointRef()}
                    onExpandedChange={this.handleRowExpanded}
                    expanded={this.state.expanded}
                    activeId={this.state.selected}
                    SubComponent={() => <ContractHistoryList history={this.props.contractHistory}/>}
                />
            </div>

            <Footer/>
        </div>
    }

    private statusFilter = Filter.TooltipFilter.bind(this, this.props.t('contractStatus'));
    private executionStatusFilter = Filter.SortFilter.bind(this, this.props.t('executionStatus'));
    private noContractFilter = Filter.InputTextFilter.bind(this, this.props.t('noContract'), true);
    private noOrderFilter = Filter.InputTextFilter.bind(this, this.props.t('noOrder'), true);
    private vinFilter = Filter.InputTextWithDefaultValue.bind(this, 'VIN', this.props.selectedContractVin || '');
    private creationDateFilter = Filter.SortFilter.bind(this, this.props.t('insertToSystemDate'));
    private plannedEndDateFilter = Filter.SortFilter.bind(this, this.props.t('plannedEndDate'));
    private endDateFilter = Filter.SortFilter.bind(this, this.props.t('contractEndDate'));

    private columns = [
        {
            id: 'active',
            accessor: 'active',
            Header: 'hiddenHeader',
            className: 'cell',
            maxWidth: 120,
            Cell: ({value}) => this.props.t(`tables:${value ? ActiveType.ACTIVE : ActiveType.NOT_ACTIVE}`),
            Filter: this.statusFilter
        },
        {
            Header: 'hiddenHeader',
            id: 'executionStatus',
            accessor: 'executionStatus',
            className: 'cell copyable',
            Filter: this.executionStatusFilter,
            Cell: ({value}) => this.props.t(`tables:${value}`)
        },
        {
            Header: 'hiddenHeader',
            id: 'contractNumber',
            accessor: 'contractNumber',
            className: 'cell copyable',
            Cell: props => Cell.CutTextCell(props, this.props.copiedToClipboard),
            Filter: this.noContractFilter
        },
        {
            Header: 'hiddenHeader',
            id: 'orderNumber',
            accessor: 'terminalInstallationOrderNumber',
            className: 'cell copyable',
            Cell: props => Cell.CutTextCell(props, this.props.copiedToClipboard),
            Filter: this.noOrderFilter
        },
        {
            Header: 'hiddenHeader',
            id: 'vin',
            accessor: 'vin',
            Filter: this.vinFilter,
            className: 'cell copyable',
            Cell: props => Cell.CutTextCell(props, this.props.copiedToClipboard)
        },
        {
            Header: 'hiddenHeader',
            id: 'creationDate',
            accessor: 'creationDate',
            Filter: this.creationDateFilter,
            className: 'cell copyable',
            Cell: props => Cell.CutDataTextCall(props, this.props.copiedToClipboard)
        },
        {
            Header: 'hiddenHeader',
            id: 'plannedEndDate',
            accessor: 'plannedEndDate',
            Filter: this.plannedEndDateFilter,
            className: 'cell copyable',
            Cell: props => Cell.CutDataTextCall(props, this.props.copiedToClipboard)
        },
        {
            Header: 'hiddenHeader',
            id: 'endDate',
            accessor: 'endDate',
            Filter: this.endDateFilter,
            className: 'cell copyable',
            Cell: props => Cell.CutDataTextCall(props, this.props.copiedToClipboard)
        },
        {
            expander: true,
            Header: 'hiddenHeader',
            className: 'cell',
            width: 65,
            Expander: Cell.Expander
        }
    ];

    private optionList = [
        {
            value: true,
            label: this.props.t(`tables:${ActiveType.ACTIVE}`),
            number: 1,
            img: Dictionary.UserStatusIcon.get(UserModule.UserEnumTypes.Status.ACTIVE)
        },
        {
            value: false,
            label: this.props.t(`tables:${ActiveType.NOT_ACTIVE}`),
            number: 1,
            img: Dictionary.UserStatusIcon.get(UserModule.UserEnumTypes.Status.BLOCKED)
        }
    ];
}

function mapDispatchToProps(dispatch: any): ConnectedDispatch {
    return {
        getContractList: (queryParams?: URLSearchParams, filterQuery?: boolean) => dispatch(ContractModule.Actions.getContractList(queryParams, filterQuery)),
        getContractHistory: (id: number) => dispatch(ContractModule.Actions.getContractHistory(id)),
        setActive: (contractNumber: string) => dispatch(ContractModule.Actions.setActive(contractNumber)),
        copiedToClipboard: (text: string) => dispatch(ErrorActions.setNewsForMoment({
            type: ErrorEnumType.NewsType.SUCCESS,
            message: i18n.t('forms:copied') + ': ' + text
        })),
        setInfinityTableLoading: (value: boolean) => dispatch(InfinityTableModule.Actions.setInfinityTableLoading(value)),
        setCurrentContractVin: (currentVin) => dispatch(ContractModule.Actions.setCurrentContractVin(currentVin))
    };
}

function mapStateToProps(state: RootState): ConnectedProps {
    return {
        contractList: state.contractStore.contractList,
        contractTotalCount: state.infinityTableStore.totalCount,
        contractHistory: state.contractStore.contractHistory,
        selectedContractVin: state.contractStore.currentVin
    };
}

export default withTranslation(['forms', 'tables', 'dictionary'])(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(ContractList) as React.ComponentType<any>
);



// WEBPACK FOOTER //
// ./src/components/Contract/ContractList.tsx